// hooks/useFollowFormFeatures.tsx
// フォロー編集モーダルのアシスト入力・音声入力・ファイルアップロード共有ロジック
//
// アシスト入力（apto enquete）の取得・初期化・描画・挿入は useAssistInput フックに委譲し、
// このフックではフォロー固有の配線（挿入先＝フォロー内容欄、モバイルキーボード対応）と
// 音声入力・ファイルアップロードを担当する。

import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, Upload, Progress, List, message } from 'antd';
import { CloseOutlined, FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import VoiceInputModal, { VoiceInputModalHandle } from '../components/VoiceInputModal';
import AssistInputModal from '../components/AssistInputModal';
import { useAssistInput } from './useAssistInput';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { API_BASE_PATH } from '../config';
import type { FollowFormValues } from '../components/FollowFormModal';

export interface UseFollowFormFeaturesOptions {
  followno?: number | null;
  baseZIndex?: number;
  onAfterUpload?: () => Promise<void> | void;
  onFileAddWithoutFollowno?: (values: FollowFormValues) => void;
  flutterFooterHeight?: number;
}

export interface UseFollowFormFeaturesReturn {
  onAssistClick: () => void;
  onVoiceClick: () => void;
  onFileAdd: (values: FollowFormValues) => void;
  onContentSetterReady: (setter: React.Dispatch<React.SetStateAction<string>>) => void;
  openFileUploadModal: (followno: number) => void;
  renderModals: () => React.ReactNode;
}

export const useFollowFormFeatures = (
  options: UseFollowFormFeaturesOptions = {},
): UseFollowFormFeaturesReturn => {
  const {
    followno = null,
    baseZIndex = 1000,
    onAfterUpload,
    onFileAddWithoutFollowno,
    flutterFooterHeight = 0,
  } = options;

  // --- refs（stale closure 回避）---
  const follownoRef = useRef<number | null>(followno ?? null);
  const uploadFollownoRef = useRef<number | null>(null);
  const contentSetterRef = useRef<React.Dispatch<React.SetStateAction<string>> | null>(null);
  // 未フォーカスのまま音声入力を「使用する」した場合は末尾に追加するための判定に使用。
  const contentTouchedRef = useRef(false);
  const voiceModalRef = useRef<VoiceInputModalHandle>(null);

  useEffect(() => {
    follownoRef.current = followno ?? null;
  }, [followno]);

  // 本文テキストエリアへのフォーカスを検知（音声入力の挿入位置判定用）
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      if ((e.target as HTMLElement | null)?.id === 'follow-content-textarea') {
        contentTouchedRef.current = true;
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, []);

  // --- アシスト入力（共通フック useAssistInput に委譲）---
  // 選択テキストはフォロー内容欄へ改行区切りで追記する
  const assist = useAssistInput({
    listKind: 'followContent',
    kind: 'followContent',
    hiddenInputId: 'selectEnqueteInputAid_followContent',
    defaultUserField: 'defaultfollowassistno',
    onInsert: (text: string) => {
      if (contentSetterRef.current) {
        contentSetterRef.current((prev) => (prev ? prev + '\n' + text : text));
      }
    },
    onAssistOpen: () => setupMobileKeyboardHandling(),
    bodyClassOnOpen: 'assist-modal-open',
  });

  // --- 音声入力 state ---
  const [tempContent, setTempContent] = useState('');

  // --- ファイルアップロード state ---
  const [fileUploadModalVisible, setFileUploadModalVisible] = useState(false);
  const [selectedFilesForUpload, setSelectedFilesForUpload] = useState<File[]>([]);
  const [processingModalVisible, setProcessingModalVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileIndex, setUploadingFileIndex] = useState(0);
  const [uploadingFileCount, setUploadingFileCount] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState('');

  // ----------------------------------------------------------------
  // モバイルキーボードハンドリング
  // ----------------------------------------------------------------
  const setupMobileKeyboardHandling = () => {
    if (window.innerWidth > 768) return;

    const setup = () => {
      const modals = document.querySelectorAll('.ant-modal');
      const modal = modals[modals.length - 1];
      if (!modal) return;

      const handleInputFocus = (input: Element) => {
        const modalEl = modal as HTMLElement;
        modalEl.style.position = 'fixed';
        modalEl.style.top = '0px';
        modalEl.style.left = '0';
        modalEl.style.right = '0';
        modalEl.style.margin = '0 auto';
        modalEl.style.transform = 'none';
        modalEl.style.zIndex = '9999';
        modalEl.style.width = 'auto';
        modalEl.style.maxWidth = '90vw';

        setTimeout(() => {
          const modalBody = modal.querySelector('.ant-modal-body') as HTMLElement;
          if (modalBody && input instanceof HTMLElement) {
            void modalBody.offsetHeight;
            try {
              input.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            } catch {
              const inputTop = (input as any).offsetTop || 0;
              modalBody.scrollTo({ top: Math.max(0, inputTop - 50), behavior: 'smooth' });
            }
          }
        }, 300);
      };

      const addFocusListeners = () => {
        const inputs = modal.querySelectorAll(
          '#aptoenqform input, #aptoenqform textarea, .ant-modal-body input, .ant-modal-body textarea',
        );
        inputs.forEach(input => {
          input.removeEventListener('focus', () => handleInputFocus(input));
          input.addEventListener('focus', () => handleInputFocus(input));
        });
      };

      addFocusListeners();

      const observer = new MutationObserver(() => { setTimeout(addFocusListeners, 300); });
      observer.observe(modal, { childList: true, subtree: true });
    };

    setTimeout(setup, 1500);
    setTimeout(setup, 3000);
    setTimeout(setup, 5000);
  };

  // ----------------------------------------------------------------
  // 公開ハンドラー
  // ----------------------------------------------------------------

  const onAssistClick = assist.onAssistClick;

  const onVoiceClick = () => {
    voiceModalRef.current?.start();
  };

  const onContentSetterReady = (setter: React.Dispatch<React.SetStateAction<string>>) => {
    contentSetterRef.current = setter;
  };

  const onFileAdd = (values: FollowFormValues) => {
    const currentFollowno = uploadFollownoRef.current ?? follownoRef.current;
    if (currentFollowno) {
      openFileUploadModal(currentFollowno);
    } else {
      onFileAddWithoutFollowno?.(values);
    }
  };

  const openFileUploadModal = (followno: number) => {
    uploadFollownoRef.current = followno;
    setSelectedFilesForUpload([]);
    setFileUploadModalVisible(true);
  };

  // ----------------------------------------------------------------
  // ファイルアップロード内部ハンドラー
  // ----------------------------------------------------------------

  const handleFileSelect = (info: any) => {
    const newFiles: File[] = (info.fileList as any[])
      .filter((f: any) => f.originFileObj)
      .map((f: any) => f.originFileObj as File);
    setSelectedFilesForUpload(prev => {
      const existingNames = prev.map(f => f.name);
      return [...prev, ...newFiles.filter(f => !existingNames.includes(f.name))];
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFilesForUpload(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (files: File[], targetFollowno: number | null) => {
    if (!targetFollowno) {
      message.error('フォローNoが取得できません');
      return;
    }
    if (files.length === 0) {
      message.warning('アップロードするファイルを選択してください');
      return;
    }

    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > 50 * 1024 * 1024) {
      message.error('ファイルサイズの合計が50MBを超えています');
      return;
    }
    if (files.length > 10) {
      message.error('一度にアップロードできるファイルは最大10個までです');
      return;
    }

    setUploadProgress(0);
    setUploadingFileIndex(0);
    setUploadingFileCount(files.length);
    setUploadingFileName('');

    try {
      const companyId = (await indexedDBStorage.getItem('companyId')) || '';
      const token = (await indexedDBStorage.getItem('token')) || '';

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadingFileIndex(i + 1);
        setUploadingFileName(file.name);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('type', '1');
        formData.append('targetno', targetFollowno.toString());
        formData.append('targetoption', '');
        formData.append('companyid', companyId);
        formData.append('file', file);

        try {
          const res = await axios.post(`${API_BASE_PATH}/upload_attachment.php`, formData, {
            headers: { Authorization: `Bearer ${token}` },
            onUploadProgress: (e) => {
              if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
            },
            timeout: 30000,
          });
          if (res.data.success) {
            message.success(`ファイル「${file.name}」をアップロードしました`);
          } else {
            message.error(`ファイル「${file.name}」のアップロードに失敗しました`);
          }
        } catch (err) {
          if (axios.isAxiosError(err)) {
            if (err.code === 'ECONNABORTED') {
              message.error(`ファイル「${file.name}」のアップロードがタイムアウトしました。`);
            } else if (err.response) {
              message.error(`ファイル「${file.name}」のアップロードエラー: ${err.response.status}`);
            } else if (err.request) {
              message.error('サーバーに接続できません。ネットワーク接続を確認してください。');
              break;
            }
          } else {
            message.error(`ファイル「${file.name}」のアップロード中に予期せぬエラーが発生しました`);
          }
        }

        if (i < files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      message.success(`${files.length}個のファイルのアップロードが完了しました`);
      setSelectedFilesForUpload([]);

      if (onAfterUpload) await onAfterUpload();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          message.error(`アップロードエラー (${error.response.status}): ${JSON.stringify(error.response.data)}`);
        } else if (error.request) {
          message.error('サーバーからの応答がありません。ネットワーク接続を確認してください。');
        } else {
          message.error(`エラー: ${error.message}`);
        }
      } else {
        message.error('ファイルのアップロード中にエラーが発生しました');
      }
    }
  };

  // ----------------------------------------------------------------
  // モーダルレンダリング
  // ----------------------------------------------------------------
  const renderModals = (): React.ReactNode => {
    const fileModalZIndex = baseZIndex + 10;

    return (
      <>
        {/* フォロー用アシスト入力モーダル */}
        <AssistInputModal
          {...assist.assistModalProps}
          width={700}
          wrapClassName="selectEnqueteInputAid businesscard-assist-modal"
          bodyStyle={{ padding: 0 }}
          layout="flex"
        />

        {/* 音声入力モーダル */}
        <VoiceInputModal
          ref={voiceModalRef}
          tempContent={tempContent}
          onTempContentChange={setTempContent}
          overlayZIndex={baseZIndex + 1000}
          modalZIndex={baseZIndex + 1010}
          wrapClassName="voice-input-confirm-modal"
          onUse={(text) => {
            const textarea = document.getElementById(
              'follow-content-textarea',
            ) as HTMLTextAreaElement;
            if (textarea && contentSetterRef.current) {
              const current = textarea.value || '';
              // 本文に一度でもフォーカスしていればカーソル位置に挿入（範囲選択時は置換）
              // 未フォーカスなら末尾に追加する。
              const start = contentTouchedRef.current ? textarea.selectionStart : current.length;
              const end = contentTouchedRef.current ? textarea.selectionEnd : current.length;
              const newContent =
                current.substring(0, start) + text + current.substring(end);
              contentSetterRef.current(newContent);
              setTimeout(() => {
                const el = document.getElementById(
                  'follow-content-textarea',
                ) as HTMLTextAreaElement;
                if (el) {
                  el.focus();
                  el.setSelectionRange(start + text.length, start + text.length);
                }
              }, 0);
            } else if (contentSetterRef.current) {
              contentSetterRef.current(text);
            }
          }}
        />

        {/* ファイルアップロードモーダル（mask={false}） */}
        {fileUploadModalVisible && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: fileModalZIndex - 1 }} />
        )}
        <Modal
          title="添付ファイル追加"
          open={fileUploadModalVisible}
          onCancel={() => { setFileUploadModalVisible(false); uploadFollownoRef.current = null; }}
          centered
          width={600}
          mask={false}
          zIndex={fileModalZIndex}
          wrapStyle={
            flutterFooterHeight > 0
              ? { paddingBottom: `${flutterFooterHeight}px`, boxSizing: 'border-box' }
              : undefined
          }
          styles={
            flutterFooterHeight > 0
              ? {
                  content: {
                    maxHeight: `calc(100dvh - ${flutterFooterHeight}px - 40px)`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  },
                  body: { flex: 1, overflowY: 'auto', minHeight: 0 },
                }
              : undefined
          }
          footer={[
            <Button key="cancel" onClick={() => { setFileUploadModalVisible(false); uploadFollownoRef.current = null; }}>
              閉じる
            </Button>,
            <Button
              key="upload"
              type="primary"
              disabled={selectedFilesForUpload.length === 0}
              onClick={() => {
                const filesToUpload = [...selectedFilesForUpload];
                const targetFollowno = uploadFollownoRef.current;
                uploadFollownoRef.current = null;
                setFileUploadModalVisible(false);
                setProcessingModalVisible(true);
                setUploadProgress(0);
                setUploadingFileIndex(0);
                setUploadingFileName('');
                handleFileUpload(filesToUpload, targetFollowno).finally(() => setProcessingModalVisible(false));
              }}
            >
              アップロード
            </Button>,
          ]}
        >
          <div style={{ marginBottom: 16 }}>
            <Upload
              multiple
              beforeUpload={() => false}
              onChange={handleFileSelect}
              showUploadList={false}
              fileList={[]}
            >
              <Button icon={<UploadOutlined />}>ファイルを選択</Button>
            </Upload>
          </div>
          {selectedFilesForUpload.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p>選択中のファイル:</p>
              <List
                size="small"
                dataSource={selectedFilesForUpload}
                renderItem={(file, index) => (
                  <List.Item
                    actions={[
                      <Button
                        key="remove"
                        type="text"
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleRemoveFile(index)}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileTextOutlined />}
                      title={file.name}
                      description={
                        file.size < 1024 * 1024
                          ? `${(file.size / 1024).toFixed(0)} KB`
                          : `${(file.size / 1024 / 1024).toFixed(2)} MB`
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          )}
        </Modal>

        {/* 処理中モーダル（mask={false}） */}
        {processingModalVisible && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: fileModalZIndex - 1 }} />
        )}
        <Modal
          title="処理中"
          open={processingModalVisible}
          footer={null}
          closable={false}
          maskClosable={false}
          centered
          mask={false}
          zIndex={fileModalZIndex}
        >
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
            {uploadingFileIndex > 0 && (
              <p>{`ファイル ${uploadingFileIndex}/${uploadingFileCount}`}</p>
            )}
            <p>{uploadingFileName}</p>
            <Progress percent={uploadProgress} status="active" style={{ width: '100%' }} />
          </div>
        </Modal>
      </>
    );
  };

  return {
    onAssistClick,
    onVoiceClick,
    onFileAdd,
    onContentSetterReady,
    openFileUploadModal,
    renderModals,
  };
};
